import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { animate } from '@lit-labs/motion';
import { classMap } from 'lit/directives/class-map.js';
import type {
  CustomSlide,
  ImageSlide,
  RssNewsItem,
  Slide,
  SlideSuggestionCategory,
} from 'src/content/screens/slide-element';
import './d-custom-slide';
import './d-news-slide-fhi';
import './d-news-slide-nrk';
import './d-weather-slide';
import './d-employees-slide';
import 'src/library/editors/elements/d-select-dropdown';
import type { EditSlideDialogResult } from 'src/content/screens/edit-slide-dialog-result';
import { DEditSlideDialog } from 'src/content/screens/d-edit-slide-dialog';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import { AlertDialog } from 'src/library/components/alert-dialog';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import type { LibraryItem } from 'src/library/screen/library-item';
import { resolveAssetUrl } from './resolve-asset-url';
import { plainTextFromMaybeHtml, shorten, uuid } from 'src/utilities/text';
import type { ScreenOrderStatus } from 'src/content/screens/screen-view';
import { slideDeleteButtonStyle } from 'src/content/screens/slide-delete-button-style';
import { t } from 'src/i18n';

/**
 * Trekk ut basenavn (uten extension) fra et filnavn og forkort til
 * maks ~30 tegn med ellipse hvis det er lengre. Brukes når slidene
 * fra en PowerPoint-opplasting skal gis lesbare navn.
 */
function shortenFilename(filename: string, maxLen = 30): string {
  const dot = filename.lastIndexOf('.');
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  return base.length > maxLen ? base.slice(0, maxLen - 1) + '…' : base;
}

@customElement('d-slide-playlist-editor')
export class DSlidePlaylistEditor extends LitElement {
  static styles = [
    slideDeleteButtonStyle,
    css`
      #playlist {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      .playlist-item {
        position: relative;
        width: 170px;
        height: 136px;
        box-sizing: border-box;
        cursor: default;
      }
      .dragging {
        opacity: 0.2;
      }
      .slide {
        border: 1px solid silver;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
      }
      img.slide {
        display: block;
        width: 100%;
        aspect-ratio: 16/9;
        object-fit: cover;
      }
      .thumbnail-wrapper {
        position: relative;
      }
      .thumbnail-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        box-sizing: border-box;
        border: 1px solid silver;
        border-radius: 6px;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column-reverse;
        align-items: flex-end;
        background: linear-gradient(
          0deg,
          rgba(255, 255, 255, 0.84) 1%,
          rgba(255, 255, 255, 0.84) 40%,
          rgba(255, 255, 255, 0) 60%,
          rgba(255, 255, 255, 0) 100%
        );
        overflow: hidden;
        cursor: pointer;
      }
      .slide-name {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 100%;
        height: 40px;
        padding: 6px;
        font-size: 13px;
        line-height: 120%;
        hyphens: auto;
        text-align: center;
        text-shadow:
          0 0 6px white,
          0 0 6px white;
      }
      d-select-dropdown {
        flex: 1;
        padding-top: 8px;
        text-align: center;
      }
      .add-buttons {
        display: flex;
        gap: 12px;
        border: none;
        cursor: default;
      }
      .add-button {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        border: 2px dashed silver;
        border-radius: 6px;
        padding: 10px 6px;
        cursor: pointer;
      }
      .add-button.custom {
        background: white url(/images/slide-icon.png) 50% -14px no-repeat;
        background-size: 100px auto;
      }
      .add-button.power-point {
        background: white url(/images/powerpoint.png) 50% 10px no-repeat;
        background-size: 50px auto;
      }
      .add-button.disabled {
        opacity: 0.5;
        cursor: default;
        pointer-events: none;
      }
      .add-button .text {
        font-size: 15px;
        line-height: 130%;
        color: var(--linkColorGray);
        text-align: center;
      }
      @media (hover: hover) {
        .add-button:not(.disabled):hover {
          border-style: solid;
          color: var(--themeColor);
        }
      }
      .add-button:not(.disabled):hover .text {
        color: black;
      }
      .pending-slide {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        aspect-ratio: 16/9;
        border: 1px solid silver;
        border-radius: 6px;
        background: hsl(0, 0%, 96%);
        color: var(--linkColorGray);
      }
      .pending-slide .spinner {
        width: 28px;
        height: 28px;
        border: 3px solid hsla(0, 0%, 0%, 0.12);
        border-top-color: var(--themeColor);
        border-radius: 50%;
        animation: d-playlist-spin 0.9s linear infinite;
      }
      .pending-slide .label {
        font-size: 12px;
        line-height: 130%;
        text-align: center;
        padding: 0 6px;
      }
      @keyframes d-playlist-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ];

  @property({ type: String })
  orderStatus: ScreenOrderStatus = 'none';
  @property({ type: Array })
  playlist: Slide[] = [];
  @property({ type: Array })
  templates: CustomSlide[] = [];
  @property({ type: Array })
  newsFeed: RssNewsItem[] = [];
  @property({ type: Array })
  newsFeedFhi: RssNewsItem[] = [];
  @property({ type: Array })
  suggestionsSelected: Slide[] = [];
  @property({ type: Array })
  suggestionsCategorized: SlideSuggestionCategory[] = [];
  @property({ type: Array })
  media: string[] = [];
  @property({ type: Array })
  employeesPresenceStatus: EmployeePresenceStatus[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: Number })
  screenId = 0;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: String })
  previewUrl = '';
  @property({ type: String })
  assetBase = '';
  @state() private dragIdx: number | null = null;
  @state() private pendingUploadName: string | null = null;
  /** Originalt filnavn (uten extension) for pågående PowerPoint-opplasting,
      brukes til å generere lesbare slidenavn med sidenummer. */
  @state() private pendingUploadDisplayName: string | null = null;

  private get limitReached() {
    return this.orderStatus === 'none' && this.playlist.length > 3;
  }

  private durationOptions(item: Slide): SelectDropdownOption[] {
    if (item.type === 'news') {
      return [
        {
          value: item.itemDuration * item.itemsCount + '',
          text: item.itemDuration * item.itemsCount + t('screens.playlistEditor.secondsPlural'),
        },
      ];
    }
    const list = Array.from({ length: 20 }, (_, i) => i + 1);
    const customDuration: number | undefined =
      !Number.isInteger(item.duration) || item.duration > 20 ? item.duration : undefined;
    const options: { value: string; text: string }[] = [];
    list.forEach((l) => {
      const unit = l === 1 ? t('screens.playlistEditor.secondsSingular') : t('screens.playlistEditor.secondsPlural');
      options.push({ value: l + '', text: l + unit });
      if (customDuration && ((l === 20 && customDuration > 20) || (customDuration > l && customDuration < l + 1))) {
        options.push({ value: customDuration + '', text: customDuration + t('screens.playlistEditor.secondsPlural') });
      }
    });
    return options;
  }

  private durationValue(item: Slide): string {
    if (item.type === 'news') {
      return item.itemDuration * item.itemsCount + '';
    }
    return item.duration + '';
  }

  private _onDragStart(e: DragEvent, index: number) {
    this.dragIdx = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.dropEffect = 'move';
    }
  }

  private _onDragOver(e: DragEvent, index: number) {
    e.preventDefault();
    if (this.dragIdx === null || this.dragIdx === index) return;
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const mouseX = e.clientX;
    const midpoint = rect.left + rect.width / 2;
    const shouldMoveLeft = this.dragIdx > index && mouseX < midpoint;
    const shouldMoveRight = this.dragIdx < index && mouseX > midpoint;
    if (shouldMoveLeft || shouldMoveRight) {
      const newPlaylist = [...this.playlist];
      const [movedItem] = newPlaylist.splice(this.dragIdx, 1);
      newPlaylist.splice(index, 0, movedItem);
      this.playlist = newPlaylist;
      this.dragIdx = index;
    }
  }

  private _onDragEnd(slide: Slide) {
    this.dragIdx = null;
    this.fireChange(slide.id);
  }

  async onPlaylistItemClick(item: Slide | undefined = undefined) {
    const result: EditSlideDialogResult = await DEditSlideDialog.open({
      slide: item,
      templates: this.templates,
      suggestionsSelected: this.suggestionsSelected,
      suggestionsCategorized: this.suggestionsCategorized,
      newsFeed: this.newsFeed,
      newsFeedFhi: this.newsFeedFhi,
      media: this.media,
      library: this.library,
      employeesPresenceStatus: this.employeesPresenceStatus,
      screenId: this.screenId,
      organizationId: this.organizationId,
      previewUrl: this.previewUrl,
      assetBase: this.assetBase,
    });
    if (result.action === 'done') {
      if (result.newSlide && result.slide) {
        this.playlist = [...this.playlist, result.slide];
      } else {
        this.playlist = [...this.playlist].map((item) => {
          if (result.slide && item.id === result.slide.id) {
            return result.slide;
          }
          return item;
        });
      }
      const slideId = result.slide?.id ?? undefined;
      this.fireChange(slideId);
    }
  }

  private deleteSlide(e: Event, id: string) {
    // Klikk-event bobbler opp til thumbnail-overlay som ellers ville åpnet editoren.
    e.stopPropagation();
    this.playlist = [...this.playlist].filter((item) => {
      return item.id !== id;
    });
    this.fireChange(undefined);
  }

  private onDurationChanged(e: CustomEvent<{ value: string }>, id: string) {
    const duration = Number(e.detail.value);
    this.playlist = this.playlist.map((item) => (item.id === id ? { ...item, duration } : item));
    this.fireChange(this.playlist.find((item) => item.id === id)?.id ?? undefined);
  }

  private uploadPresentation() {
    // En pågående opplasting setter `pendingUploadName`, og `updated()` matcher
    // på det navnet når biblioteket oppdateres. To samtidige opplastinger ville
    // overskrevet hverandres state — blokker ny opplasting mens en pågår.
    if (this.pendingUploadName) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept =
      '.pptx,.ppt,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const result: AlertDialogResult = await AlertDialog.open({
        title: t('screens.playlistEditor.uploadPowerPointTitle'),
        content: t('screens.playlistEditor.uploadPowerPointContent'),
      });
      if (result.action !== 'ok') return;
      const dot = file.name.lastIndexOf('.');
      const ext = dot >= 0 ? file.name.slice(dot) : '';
      const uniqueName = `${uuid()}${ext}`;
      // Fila lastes opp direkte til S3 via presignert URL (#13237) — vi sender
      // selve File-objektet, ingen base64 dataUrl.
      const mimeType = file.type || 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      this.pendingUploadName = uniqueName;
      this.pendingUploadDisplayName = shortenFilename(file.name);
      this.dispatchEvent(
        new CustomEvent<{ file: File; name: string; mimeType: string }>('upload-presentation', {
          bubbles: true,
          composed: true,
          detail: { file, name: uniqueName, mimeType },
        }),
      );
    };
    input.click();
  }

  protected updated(changed: Map<string, unknown>) {
    if (!this.pendingUploadName) return;
    if (!changed.has('library')) return;
    const item = this.library.find((i) => i.name === this.pendingUploadName);
    if (!item || !item.assets || item.assets.length === 0) return;
    const baseName = this.pendingUploadDisplayName ?? item.name;
    const newSlides: ImageSlide[] = item.assets.map((asset, idx) => ({
      type: 'image',
      id: uuid(),
      name: t('screens.playlistEditor.slidePageName', { baseName, number: idx + 1 }),
      duration: item.defaultDuration || 10,
      media: asset,
      assets: [asset],
    }));
    this.playlist = [...this.playlist, ...newSlides];
    this.pendingUploadName = null;
    this.pendingUploadDisplayName = null;
    this.fireChange(newSlides[0].id);
  }

  private fireChange(slideId: string | undefined) {
    this.dispatchEvent(
      new CustomEvent('playlist-changed', {
        bubbles: true,
        composed: true,
        detail: {
          playlist: this.playlist,
          slideIdForPreview: slideId,
        },
      }),
    );
  }

  private order() {
    this.dispatchEvent(
      new CustomEvent('request-order', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  renderThumbnail(item: Slide) {
    if (item.type === 'custom') {
      return html` <d-custom-slide class="slide" .slide=${item} .assetBase=${this.assetBase}></d-custom-slide>`;
    }
    if (item.type === 'fhi') {
      return html`<d-news-slide-fhi class="slide" .slide=${item} .feed=${this.newsFeedFhi}></d-news-slide-fhi>`;
    }
    if (item.type === 'news') {
      return html`<d-news-slide-nrk class="slide" .slide=${item} .feed=${this.newsFeed}></d-news-slide-nrk>`;
    }
    if (item.type === 'weather') {
      return html`<d-weather-slide class="slide" .slide=${item} .assetBase=${this.assetBase}></d-weather-slide>`;
    }
    if (item.type === 'employees') {
      return html`<d-employees-slide
        class="slide"
        .slide=${item}
        .employees=${this.employeesPresenceStatus}
      ></d-employees-slide>`;
    }
    if (item.type === 'image') {
      const url = resolveAssetUrl(item.media, this.assetBase);
      return html`<img class="slide" src="${url}" />`;
    }
    return nothing;
  }

  render() {
    return html`
      <div id="playlist">
        ${repeat(
          this.playlist,
          (item) => item.id,
          (item, index) => {
            const isDragging = this.dragIdx === index;
            const plainName = plainTextFromMaybeHtml(item.name);
            return html`
              <div
                ${animate({
                  disabled: isDragging,
                  keyframeOptions: {
                    duration: 350,
                    easing: 'ease-in-out',
                    fill: 'both',
                  },
                })}
                class=${classMap({
                  'playlist-item': true,
                  dragging: isDragging,
                })}
                draggable="true"
                @dragstart=${(e: DragEvent) => this._onDragStart(e, index)}
                @dragover=${(e: DragEvent) => this._onDragOver(e, index)}
                @dragend=${() => this._onDragEnd(item)}
              >
                <div class="thumbnail-wrapper">
                  ${this.renderThumbnail(item)}
                  <div class="thumbnail-overlay" @click=${() => this.onPlaylistItemClick(item)}>
                    <div class="slide-name">
                      ${plainName ? shorten(plainName, 40) : t('screens.playlistEditor.untitled')}
                    </div>
                    <button class="delete" @click=${(e: Event) => this.deleteSlide(e, item.id)}></button>
                  </div>
                </div>
                <d-select-dropdown
                  controller
                  .disabled=${item.type === 'news'}
                  .options=${this.durationOptions(item)}
                  .value=${this.durationValue(item)}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => this.onDurationChanged(e, item.id)}
                ></d-select-dropdown>
              </div>
            `;
          },
        )}
        ${this.pendingUploadName
          ? html`
              <div
                class="playlist-item"
                ${animate({
                  keyframeOptions: {
                    duration: 350,
                    easing: 'ease-in-out',
                    fill: 'both',
                  },
                })}
              >
                <div class="pending-slide" role="status" aria-live="polite">
                  <div class="spinner"></div>
                  <div class="label">${t('screens.playlistEditor.converting')}</div>
                </div>
              </div>
            `
          : nothing}
        <div
          ${animate({
            keyframeOptions: {
              duration: 350,
              easing: 'ease-in-out',
              fill: 'both',
            },
          })}
          class="add-buttons"
        >
          <div
            class=${classMap({
              'playlist-item': true,
              'add-button': true,
              custom: true,
            })}
            @click=${() => (this.limitReached ? this.order() : this.onPlaylistItemClick())}
          >
            <div class="text">
              ${this.limitReached ? t('screens.playlistEditor.buyModule') : t('screens.playlistEditor.addSlide')}
            </div>
          </div>
          <div
            class=${classMap({
              'playlist-item': true,
              'add-button': true,
              'power-point': true,
              disabled: this.pendingUploadName !== null || this.limitReached,
            })}
            @click=${() => (this.pendingUploadName === null ? this.uploadPresentation() : undefined)}
          >
            <div class="text">${t('screens.playlistEditor.uploadPowerPointButton')}</div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-slide-playlist-editor': DSlidePlaylistEditor;
  }
}
